import React, { SetStateAction, useState } from "react";
import { Box, Button, TextField, Typography, IconButton, FormControl, InputLabel } from "@mui/material";
import LinkIcon from "@mui/icons-material/Link";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import { knowledgeDoc } from "../../../Shared-ui/Types/KnowledgeBase";
import CloseIcon from "@mui/icons-material/Close";
import { useDispatch } from "react-redux";
import { ShowError } from "../../../Shared-ui/Store/MessageReducer/MsgActions";

interface MyComponentProps {
  setKnowledgeBaseDoc: React.Dispatch<SetStateAction<knowledgeDoc>>
}


const DocumentTab: React.FC<MyComponentProps> = (props) => {
  const [pdfUrl, setPdfUrl] = useState("");
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([])
  const dispatch = useDispatch()

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files) {
      // const allowedExtensions = /\.(pdf|doc|docx|json|csv|xls|xlsx)$/i;
      const allowedExtensions = /\.(pdf|doc|docx|json|csv)$/i;
      const fileArr = Array.from(files);
      const validFiles = fileArr.filter(file => allowedExtensions.test(file.name));
      const invalidFiles = fileArr.filter(file => !allowedExtensions.test(file.name));

      if (invalidFiles.length > 0) {
        dispatch(
          ShowError(
            `Unsupported file type: ${invalidFiles.map(f => f.name).join(", ")}. Allowed: pdf, doc, docx, json, csv.`
          )
        );
      }
      setSelectedFiles(validFiles);
      props.setKnowledgeBaseDoc(preVal => ({ ...preVal, files: validFiles, url: pdfUrl }));
      // const fileArr = Array.from(files)
      // setSelectedFiles(fileArr)
      // props.setKnowledgeBaseDoc(preVal => ({ ...preVal, files: fileArr, url: pdfUrl }))
    }
  };

  const handleBrowseClick = (event: React.MouseEvent) => {
    event.stopPropagation(); // Prevent event bubbling
    fileInputRef.current?.click();
  };

  return (
    <Box>
      <Box
        component="div"
        sx={{
          border: "2px dashed #ccc",
          borderRadius: 2,
          padding: 4,
          textAlign: "center",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: .5,
          cursor: "pointer",
          bgcolor: "#fafbfc",
          transition: "border-color 0.2s",
          "&:hover": { borderColor: "primary.main" },
        }}
        onClick={handleBrowseClick}
        onDrop={e => {
          e.preventDefault();
          e.stopPropagation();
          const files = Array.from(e.dataTransfer.files)
            .filter(file =>
              file.type === "application/pdf" ||
              file.name.toLowerCase().endsWith(".pdf") ||
              file.type === "application/vnd.openxmlformats-officedocument.wordprocessingml.document" ||
              file.name.toLowerCase().endsWith(".docx")
            );
          if (files.length) {
            setSelectedFiles((preVal) => ([...preVal, ...files]));
            props.setKnowledgeBaseDoc(preVal => ({ ...preVal, files: [...preVal.files || [], ...files], url: pdfUrl }));
          }
        }}
        onDragOver={e => {
          e.preventDefault();
          e.stopPropagation();
        }}
      >
        {/* Hidden File Input */}
        <input
          type="file"
          multiple
          accept=".pdf,application/pdf,
        .doc,.docx,application/msword,
        application/vnd.openxmlformats-officedocument.wordprocessingml.document,
        .json,application/json,
        .csv,text/csv"
          ref={fileInputRef}
          onChange={handleFileUpload}
          style={{ display: "none" }}
        />
        {/*  .xls,application/vnd.ms-excel,
        .xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet */}
        <IconButton
          aria-label="upload"
          sx={{
            padding: "6px",
            borderRadius: "6px",
            "&:hover": { backgroundColor: "#f0f0f0" },
          }}
          onClick={handleBrowseClick}
        >
          <DescriptionOutlinedIcon sx={{ fontSize: "2rem", color: "primary.main" }} />
        </IconButton>
        <Typography variant="body1" color="textSecondary">
          Drag and drop documents here or click to upload
        </Typography>
        <Typography variant="caption" gutterBottom color="textSecondary">
          Supported file types: <strong>PDF, DOC, DOCX, JSON, CSV</strong>.
        </Typography>
        <Button
          variant="contained"
          disableElevation
          size="large"
          sx={{ textTransform: "capitalize" }}
          onClick={handleBrowseClick}
        >
          Browse Files
        </Button>
        <Box
        sx={{
             maxHeight: 200,
             overflowY: "auto",
             width: "100%",
             mb: 2, 
             px: 1,
            }}
          >
        {selectedFiles.map((file, index) => (
          <Box
            key={index}
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              width: "100%",
              bgcolor: "#f5f5f5",
              borderRadius: 1,
              px: 2,
              py: 0.5,
              mt: 1,
            }}
          >
            <Typography variant="body2" sx={{ flex: 1, wordBreak: "break-all" }}>
              {file.name}
            </Typography>
            <IconButton
              size="small"
              onClick={e => {
                e.stopPropagation();
                setSelectedFiles(prev => prev.filter((_, i) => i !== index));
                props.setKnowledgeBaseDoc(preVal => ({
                  ...preVal,
                  files: (preVal.files ?? []).filter((_, i) => i !== index),
                  url: pdfUrl,
                }));
                if (fileInputRef.current) {
                  fileInputRef.current.value = "";
                }
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        ))}
        </Box>
      </Box>

      {/* PDF URL Input */}
      <Box sx={{ mt: 2, display: "flex", alignItems: "flex-start", gap: 2 }}>
        <FormControl fullWidth variant="outlined">
          <InputLabel shrink htmlFor="pdf-url" sx={{ background: "#fff", px: 0.5 }}>
            Or use Documents URLs
          </InputLabel>
          <TextField
            id="pdf-url"
            required
            placeholder="Enter Documents URL here"
            variant="outlined"
            fullWidth
            value={pdfUrl}
            onChange={(e) => setPdfUrl(e.target.value)}
            sx={{ minHeight: "56px" }}
            inputProps={{ maxLength: 2048 }}
          />
        </FormControl>

        {/* Add Button */}
        <Button
          variant="contained"
          disableElevation
          size="large"
          sx={{
            textTransform: "capitalize",
            height: "56px",
          }}
          startIcon={<LinkIcon />}
        >
          Add
        </Button>
      </Box>
    </Box>
  );
};

export default DocumentTab;
